import React from "react";
import A4PageFrame from "./A4PageFrame";
import { fmtNum, fmtPct, fmtDate } from "./formatters";

// Phase 3 (M2) — "Résultat détaillé", one section per lease, rendered PORTRAIT.
//
// WHY PORTRAIT: the client's reference report renders this section portrait, with a
// whole object (all its cost accounts + Total/Acomptes/Solde + the 5-row
// Consommations block) on ONE page. Étang B has ~26 cost accounts; a portrait A4
// body fits ~47 rows (ROWS_PER_PAGE_PORTRAIT), so the whole object fits one page —
// whereas the short landscape body (~23 rows) split each object across 3 pages and
// tripled the report length. The cost + consommations tables use width:100% so they
// reflow to the portrait content width and do NOT clip on the right (verified against
// the reference portrait export — all 7 columns render).
//
// WHY PAGINATION SURVIVES: a rare object with more cost lines than fit one portrait
// page still splits via splitObjectDetail() — the object header + column header
// REPEAT on each page, and the Total/Acomptes/Solde + 5-row Consommations block
// render ONLY on the last page (never split/clipped). objectDetailPageCount() returns
// the page count so Preview.js's page-number + bookmark math agrees exactly.
//
// Consommations columns (client reference):
//     Consommations [energy + unit inline] | <year> | CHF/unité | Total Immeuble | Part %
// Fixed 5 rows: Chauffage kWh · Climatisation kWh · Eau m3 · Eau chaude kWh · Electricité kWh

// Pixel-accurate row budget for portrait ObjectDetail — RECOMPUTED after the
// 2026-07-21 client ask ("reduce space between lines so all costs fit on 1
// page"). Row style was tightened to padding 1/lineHeight 12 → 15 px per row.
//
// Recomputed against the 15 px row height:
//   Content band                                 936 px
//   Header block (5 Info rows + margins)        -85  px
//   Cost column header (th, 2+10+2+1)           -17  px
//   Tail (Acomptes OFF): Total CHF (15) + margin (12) +
//     Conso header (17) + 5 rows (75)           -134 px
//   Tail (Acomptes ON): + Acomptes (15) + Solde (15)  = 164 px
//   Cost-row budget on last page (OFF) = (936-85-17-134)/15 = 46 rows
//   Cost-row budget on last page (ON)  = (936-85-17-164)/15 = 44 rows
//   Cost-row budget on middle pages    = (936-85-17)/15     = 55 rows
//
// Round each budget down by 2 rows as a safety cushion against sub-pixel
// rounding during html-to-image capture. Étang A (41 cost accounts) now fits
// ON A SINGLE PAGE for both toggle states (41 ≤ 42 acomptes-on, 41 ≤ 44
// acomptes-off). Étang B (26 costs) fits comfortably on one page too. Larger
// buildings past 42 costs (rare) paginate cleanly across middle+last pages.
const COST_ROWS_ON_LAST_PAGE_ACOMPTES_OFF = 44; // real 46 minus 2-row cushion
const COST_ROWS_ON_LAST_PAGE_ACOMPTES_ON = 42;  // real 44 minus 2-row cushion
const COST_ROWS_PER_MIDDLE_PAGE = 53;           // real 55 minus 2-row cushion

// Split one object's cost lines into page-sized chunks. Returns [{ lines, isLast }].
// The last chunk (isLast) carries the totals + consommations, so it reserves room for
// them. Guarantees ≥1 chunk (zero-cost object → one page). The last-page cost
// budget shrinks by 2 rows when acomptesEnabled=true because the Acomptes and
// Solde rows land on the last page too (see comment above).
export function splitObjectDetail(d, acomptesEnabled = true) {
  const costLines = d.cost_lines || [];
  const n = costLines.length;
  const lastBudget = acomptesEnabled
    ? COST_ROWS_ON_LAST_PAGE_ACOMPTES_ON
    : COST_ROWS_ON_LAST_PAGE_ACOMPTES_OFF;
  // Single page when all cost lines fit alongside the tail.
  if (n <= lastBudget) {
    return [{ lines: costLines, isLast: true }];
  }
  // Otherwise: fill middle pages (header only) until the remainder fits the last
  // page (which must also hold the tail). Keep the last page's cost lines ≤
  // lastBudget by carving them off the end first.
  const chunks = [];
  const lastPageLines = costLines.slice(n - lastBudget);
  const middleLines = costLines.slice(0, n - lastBudget);
  for (let i = 0; i < middleLines.length; i += COST_ROWS_PER_MIDDLE_PAGE) {
    chunks.push({ lines: middleLines.slice(i, i + COST_ROWS_PER_MIDDLE_PAGE), isLast: false });
  }
  chunks.push({ lines: lastPageLines, isLast: true });
  return chunks;
}

// Number of landscape pages this object occupies. Preview.js imports this so the
// page-number + bookmark math never drifts from the actual render.
export function objectDetailPageCount(d, acomptesEnabled = true) {
  return splitObjectDetail(d, acomptesEnabled).length;
}

const ObjectDetail = ({
  data,
  capped = false,
  omittedCount = 0,
  capThreshold = 0,
  building,
  periodLabel,
  periodYears,
  whiteLabelling,
  logoUrl,
  acomptesEnabled = true,
  basePageNumber = 1,
  grandTotalPages = 1,
}) => {
  const details = data?.details || [];

  // Large-building guard: past the cap the per-object detail pages are omitted to
  // keep the browser-side PDF export from exhausting memory. Render a single note
  // page in their place (all summary sections are still produced). Kept in sync
  // with Preview.js (DETAIL_PAGE_CAP → objectDetailPages = 1 when capped).
  if (capped) {
    return (
      <A4PageFrame
        building={building}
        periodLabel={periodLabel}
        sectionTitle="Résultat détaillé"
        whiteLabelling={whiteLabelling}
        logoUrl={logoUrl}
        pageNumber={basePageNumber}
        totalPages={grandTotalPages}
        resultsPage
        hidePageNumber
      >
        <div style={{ marginTop: 24, fontSize: 11, lineHeight: "18px", color: "#00033F", maxWidth: 620 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            Résultat détaillé par objet — non inclus
          </div>
          <div>
            Cet immeuble comporte {fmtNum(omittedCount, { decimals: 0 })} objets. Pour préserver la
            lisibilité et la performance de génération du rapport, les pages
            « Résultat détaillé » par objet ne sont pas incluses au-delà de{" "}
            {fmtNum(capThreshold, { decimals: 0 })} objets. Toutes les sections de synthèse
            (Modules, Résultats par module, par objet et par locataire) restent complètes.
          </div>
        </div>
      </A4PageFrame>
    );
  }

  if (details.length === 0) return null;

  const yearLabel = periodYears?.current ?? "";
  let pageNo = basePageNumber;
  const frames = [];

  details.forEach((d, idx) => {
    const chunks = splitObjectDetail(d, acomptesEnabled);
    chunks.forEach((chunk, chunkIdx) => {
      // Client request: no "(x/N)" running counter in the title and no bottom page
      // number — this section is delivered per-tenant. Title stays "Résultat détaillé";
      // continuation pages get a "(suite)" hint so a multi-page object reads clearly.
      const label = chunkIdx === 0 ? "Résultat détaillé" : "Résultat détaillé (suite)";

      frames.push(
        <A4PageFrame
          key={`${d.objet_code || idx}-${chunkIdx}`}
          building={building}
          periodLabel={periodLabel}
          sectionTitle={label}
          whiteLabelling={whiteLabelling}
          logoUrl={logoUrl}
          pageNumber={pageNo++}
          totalPages={grandTotalPages}
          resultsPage
          hidePageNumber
        >
          {/* Header block repeats on every page of the object. objet_code carries
              the real code (Primerose) or the full raw reference (Étang B/Sévelin). */}
          <div style={{ marginBottom: 10 }}>
            <Info label="Objet" value={d.objet_code || d.reference} />
            <Info label="Locataire" value={d.locataire || "Vacant"} />
            <Info label="Module" value={d.module} />
            <Info label="Période" value={`${fmtDate(d.fee_start)} - ${fmtDate(d.fee_end)}`} />
            <Info label="Surface m²" value={fmtNum(d.surface_m2)} />
          </div>

          {/* Cost-line breakdown — width:100% so it reflows to the portrait content
              width and never overflows the capture box. This chunk's cost lines. */}
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #00033F" }}>
                <th style={th}>Compte</th>
                <th style={th}>Désignation</th>
                <th style={thNum}>{yearLabel} HT</th>
                <th style={thNum}>CHF/m2</th>
                <th style={thNum}>{yearLabel} TTC</th>
                <th style={thNum}>Total HT</th>
                <th style={thNum}>Part %</th>
              </tr>
            </thead>
            <tbody>
              {/* Client (round 5): accounts the object doesn't bear show "-" (not
                  0.00). Plain fmtNum/fmtPct render 0 and null alike as "-". */}
              {chunk.lines.map((c, i) => (
                <tr key={i}>
                  <td style={td}>{c.cost}</td>
                  <td style={tdEllipsis}>{c.designation || ""}</td>
                  <td style={tdNum}>{fmtNum(c.ht)}</td>
                  <td style={tdNum}>{fmtNum(c.chf_m2)}</td>
                  <td style={tdNum}>{fmtNum(c.ttc)}</td>
                  <td style={tdNum}>{fmtNum(c.total_ht)}</td>
                  <td style={tdNum}>{fmtPct(c.part_pct)}</td>
                </tr>
              ))}
              {/* Totals render only on the last chunk of the object. */}
              {chunk.isLast && (
                <>
                  <tr style={{ borderTop: "1px solid #00033F", fontWeight: 700 }}>
                    <td style={td} colSpan={2}>Total CHF</td>
                    <td style={tdNum}>{fmtNum(d.total?.ht)}</td>
                    <td style={tdNum}>{fmtNum(d.total?.chf_m2)}</td>
                    <td style={tdNum}>{fmtNum(d.total?.ttc)}</td>
                    {/* Total HT column total (building grand total) + aggregate Part %
                        (object HT / building HT) — client request. */}
                    <td style={tdNum}>{fmtNum(d.total?.total_ht_all)}</td>
                    <td style={tdNum}>{fmtPct(d.total?.part_pct)}</td>
                  </tr>
                  {/* Acomptes/Solde lines hidden when the per-period Acomptes
                      toggle is OFF (presentation only). Pagination reserves a
                      conservative tail budget, so removing these never clips. */}
                  {acomptesEnabled && (
                    <>
                      <tr>
                        <td style={td} colSpan={4}>Acomptes</td>
                        <td style={tdNum}>{fmtNum(d.acomptes)}</td>
                        <td style={tdNum} colSpan={2} />
                      </tr>
                      <tr style={{ fontWeight: 700 }}>
                        <td style={td} colSpan={4}>Solde</td>
                        <td style={tdNum}>{fmtNum(d.solde)}</td>
                        <td style={tdNum} colSpan={2} />
                      </tr>
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>

          {/* Consommations — 5 fixed rows, rendered ONLY on the object's last page so
              the block is never split/clipped. Missing meters render "-" (fmtNum(null)). */}
          {chunk.isLast && (
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #00033F" }}>
                  <th style={th}>Consommations</th>
                  <th style={thNum}>{yearLabel}</th>
                  <th style={thNum}>CHF/unité</th>
                  <th style={thNum}>Total Immeuble</th>
                  <th style={thNum}>Part %</th>
                </tr>
              </thead>
              <tbody>
                {(d.consommations || []).map((c, i) => (
                  <tr key={i}>
                    <td style={tdEllipsis}>{c.energy}{c.unit ? ` ${c.unit}` : ""}</td>
                    <td style={tdNum}>{fmtNum(c.object_value)}</td>
                    <td style={tdNum}>{fmtNum(c.chf_unite, { decimals: 3 })}</td>
                    <td style={tdNum}>{fmtNum(c.total_immeuble)}</td>
                    <td style={tdNum}>{fmtPct(c.part_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </A4PageFrame>
      );
    });
  });

  return <>{frames}</>;
};

function Info({ label, value }) {
  return (
    <div style={{ display: "flex", fontSize: 10, lineHeight: "15px" }}>
      <span style={{ width: 90, fontWeight: 600, color: "#00033F" }}>{label}</span>
      <span style={{ color: "#00033F" }}>{value}</span>
    </div>
  );
}

const th = { padding: "2px 6px", fontWeight: 600, textAlign: "left", fontSize: 10 };
const thNum = { ...th, textAlign: "right" };
// Client feedback 2026-07-21: reduce space between lines so Étang A (41 cost
// accounts) fits its Résultat détaillé on a single page. Row height math:
//   padding 1 + lineHeight 12 + padding 1 + border 1 = 15 px per row.
// Combined with the updated cost-row budgets in the pagination helpers above
// (COST_ROWS_ON_LAST_PAGE_ACOMPTES_* were recomputed against 15 px rows),
// 41 costs + 5-row Consommations + Total/Acomptes/Solde fit ONE portrait page
// even with the Acomptes toggle ON. Padding 1 px is tight but stays legible at
// fontSize 9.5 with the 12 px lineHeight cushion.
const td = { padding: "1px 6px", fontSize: 9.5, lineHeight: "12px", borderBottom: "1px solid rgba(0,3,63,0.05)" };
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };
// Désignation / energy-label cell: ellipsize long names so they never widen the
// column (landscape has ample width, but this keeps rows tidy).
const tdEllipsis = { ...td, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 320 };

export default ObjectDetail;
